import { Image, Spin, type ImageProps } from "antd";

import { isHeicUrl, useHeicImage } from "~/hooks/useHeicImage";

interface HeicImageProps extends Omit<ImageProps, "src" | "preview"> {
  src: string | undefined;
  previewSrc?: string;
}

/**
 * Image component with HEIC/HEIF format support.
 * Only processes HEIC images - regular images are rendered directly for better performance.
 */
export const HeicImage = ({ src, previewSrc, ...props }: HeicImageProps) => {
  const needsHeicProcessing = isHeicUrl(src) || isHeicUrl(previewSrc);

  // For non-HEIC images, render directly without hook overhead
  if (!needsHeicProcessing) {
    return <Image {...props} src={src} preview={{ src: previewSrc || src }} />;
  }

  return <HeicImageWithConversion src={src} previewSrc={previewSrc} {...props} />;
};

/**
 * Internal component that handles HEIC conversion
 */
const HeicImageWithConversion = ({ src, previewSrc, ...props }: HeicImageProps) => {
  const { src: convertedSrc, isLoading: isThumbnailLoading } = useHeicImage(src);
  const { src: convertedPreviewSrc, isLoading: isPreviewLoading } = useHeicImage(previewSrc || src);

  if (isThumbnailLoading) {
    return (
      <div
        style={{
          height: props.height,
          width: props.width || props.height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
          borderRadius: 6,
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  return (
    <Image
      {...props}
      src={convertedSrc}
      preview={{
        src: convertedPreviewSrc,
        ...(isPreviewLoading && { mask: <Spin /> }),
      }}
    />
  );
};
